import { useCallback, useMemo, useState } from 'react';

import {
  AlertCircleIcon,
  ArrowDown01Icon,
  CheckmarkCircle02Icon,
  Download01Icon,
  InformationCircleIcon,
  UserMultiple02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { CANDIDATE_STATUS_DISPLAY } from 'constants/statuses';
import { saveAs } from 'file-saver';
import { Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { FormMessage, MessageType, MultipleChoice } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import StatusPill from 'components/Pills/StatusPills';
import { Table } from 'components/Table/Table';

import { getBlockedCsvIndices, resolveEffectiveDecisionsByIndex } from './duplicateDecisionUtils';

// Typography only — spacing from parent flex gap + FormMessage noMargin (avoids double margins)
const reviewAlertClass = '!text-xl !font-light !text-text-secondary leading-7';

const DUPLICATE_ACTION_SKIP = 'skip';
const DUPLICATE_ACTION_CREATE_NEW = 'create_new';
const DUPLICATE_ACTION_ADD_TO_EXISTING = 'add_to_existing';

function escapeCsvValue(val) {
  if (val == null) return '';
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const EXPORT_EXCLUDE_COLUMNS = new Set(['bundle_id', 'brand_id']);

function buildSkippedRowsCsv(duplicates) {
  if (!duplicates?.length) return '';
  const first = duplicates[0];
  const row = first?.csv_row ?? {};
  const headers = Object.keys(row).filter(h => !EXPORT_EXCLUDE_COLUMNS.has(h));
  const headerLine = headers.map(escapeCsvValue).join(',');
  const dataLines = duplicates.map(dup => {
    const r = dup?.csv_row ?? {};
    return headers.map(h => escapeCsvValue(r[h])).join(',');
  });
  return [headerLine, ...dataLines].join('\r\n');
}

export const DuplicatesReview = ({
  previewResult,
  decisions = [],
  onDecisionsChange,
  entityLabel = 'candidates',
}) => {
  const singularLabel = entityLabel.endsWith('s') ? entityLabel.slice(0, -1) : entityLabel;
  const capitalizedEntityLabel = entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);
  const exactList = useMemo(
    () =>
      previewResult &&
      typeof previewResult === 'object' &&
      Array.isArray(previewResult.exact_duplicates)
        ? previewResult.exact_duplicates
        : [],
    [previewResult],
  );
  const possibleList = useMemo(
    () =>
      previewResult &&
      typeof previewResult === 'object' &&
      Array.isArray(previewResult.possible_duplicates)
        ? previewResult.possible_duplicates
        : [],
    [previewResult],
  );
  const newCount = useMemo(
    () =>
      previewResult && typeof previewResult === 'object' ? Number(previewResult.new_count) || 0 : 0,
    [previewResult],
  );

  const [duplicatesOpen, setDuplicatesOpen] = useState(false);

  const allDuplicates = useMemo(() => [...exactList, ...possibleList], [exactList, possibleList]);

  const blockedIndices = useMemo(
    () => getBlockedCsvIndices(previewResult?.validation_issues),
    [previewResult?.validation_issues],
  );

  const exactDbMatchCount = useMemo(
    () => exactList.filter(d => d?.match_reason?.startsWith('email_match')).length,
    [exactList],
  );
  const intraCsvDuplicateCount = useMemo(
    () => exactList.filter(d => d?.match_reason === 'intra_csv_duplicate').length,
    [exactList],
  );

  const allDuplicatesAreIntraCsv =
    allDuplicates.length > 0 && allDuplicates.every(d => d?.match_reason === 'intra_csv_duplicate');

  // Unique duplicate CSV row counts (possible_duplicates can have multiple entries per csv_index when multiple same-name candidates exist)
  const uniqueDuplicateRowCount = useMemo(
    () => new Set(allDuplicates.map(d => d.csv_index)).size,
    [allDuplicates],
  );
  const uniquePossibleRowCount = useMemo(
    () => new Set(possibleList.map(d => d.csv_index)).size,
    [possibleList],
  );

  // Resolve effective decision per csv_index (shared with BulkImport so import count and review counts stay in sync)
  const decisionsList = Array.isArray(decisions) ? decisions : [];
  const effectiveDecisionsByIndex = useMemo(
    () => resolveEffectiveDecisionsByIndex(decisionsList),
    [decisionsList],
  );

  const effectiveDecisions = useMemo(
    () =>
      effectiveDecisionsByIndex && typeof effectiveDecisionsByIndex === 'object'
        ? Object.values(effectiveDecisionsByIndex)
        : [],
    [effectiveDecisionsByIndex],
  );

  // Single action for all duplicate rows (derived from decisions for radio display).
  // When user picks "Update existing", some rows may be forced to "skip" (e.g. intra-CSV dup);
  // we still show "Update existing" as selected by using the highest intent present.
  const globalDuplicateAction = useMemo(() => {
    if (!effectiveDecisions?.length) return DUPLICATE_ACTION_SKIP;
    const actions = effectiveDecisions.map(d => d.action);
    if (actions.some(a => a === DUPLICATE_ACTION_ADD_TO_EXISTING))
      return DUPLICATE_ACTION_ADD_TO_EXISTING;
    if (actions.some(a => a === DUPLICATE_ACTION_CREATE_NEW)) return DUPLICATE_ACTION_CREATE_NEW;
    return DUPLICATE_ACTION_SKIP;
  }, [effectiveDecisions]);

  const setGlobalDuplicateAction = useCallback(
    action => {
      const list = Array.isArray(allDuplicates) ? allDuplicates : [];
      const next = list.map(dup => {
        const isIntraCsvDuplicate = dup.match_reason === 'intra_csv_duplicate';
        const isBlocked = blockedIndices.has(String(dup.csv_index));
        const canAddToExisting = !isIntraCsvDuplicate && !isBlocked && !!dup.existing?.id;
        let resolvedAction = action;
        if (isIntraCsvDuplicate || isBlocked) {
          resolvedAction = DUPLICATE_ACTION_SKIP;
        } else if (action === DUPLICATE_ACTION_ADD_TO_EXISTING && !canAddToExisting) {
          resolvedAction = DUPLICATE_ACTION_SKIP;
        }
        return {
          csv_index: dup.csv_index,
          action: resolvedAction,
          existing_id:
            resolvedAction === DUPLICATE_ACTION_ADD_TO_EXISTING ? dup.existing?.id : undefined,
        };
      });
      onDecisionsChange?.(next);
    },
    [allDuplicates, blockedIndices, onDecisionsChange],
  );

  const totalRows = newCount + uniqueDuplicateRowCount;
  const skipCount = effectiveDecisions.filter(d => d.action === 'skip').length;
  const createAnywayCount = effectiveDecisions.filter(
    d => d.action === 'create_new' && !blockedIndices.has(String(d.csv_index)),
  ).length;
  const addToExistingCount = useMemo(
    () =>
      new Set(
        effectiveDecisions
          .filter(
            d =>
              d.action === 'add_to_existing' &&
              d.existing_id != null &&
              !blockedIndices.has(String(d.csv_index)),
          )
          .map(d => d.existing_id),
      ).size,
    [effectiveDecisions, blockedIndices],
  );

  const handleDownloadSkipped = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      const csv = buildSkippedRowsCsv(allDuplicates);
      if (!csv) return;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `duplicate_rows_${allDuplicates.length}.csv`);
    },
    [allDuplicates],
  );

  if (!previewResult || typeof previewResult !== 'object') {
    return null;
  }

  return (
    <div className="flex flex-col gap-8 w-full text-text-primary">
      <div className="bg-white rounded-2xl border border-border-primary shadow-sm shadow-soft overflow-hidden">
        <Table.Context onQueryUpdate={() => {}} manualQueryUpdate defaultQuery={{}}>
          <Table>
            <Table.Head className="!h-auto min-h-0">
              <Table.Row>
                <Table.Header className="w-[38%] !py-4 !px-8 text-text-secondary font-light text-lg bg-surface-secondary border-b border-border-primary">
                  Status
                </Table.Header>
                <Table.Header className="w-[42%] !py-4 !px-8 text-text-secondary font-light text-lg bg-surface-secondary border-b border-border-primary">
                  Description
                </Table.Header>
                <Table.Header
                  align="right"
                  className="w-[20%] !py-4 !px-8 text-text-secondary font-light text-lg bg-surface-secondary border-b border-border-primary">
                  Count
                </Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Col className="w-[38%] !p-8 align-middle border-b border-border-primary">
                  <span className="inline-flex items-center gap-4 text-xl font-medium text-text-primary">
                    <HugeiconsIcon
                      icon={CheckmarkCircle02Icon}
                      className="text-green shrink-0"
                      size={22}
                      strokeWidth={1.5}
                    />
                    <span>New {capitalizedEntityLabel}</span>
                  </span>
                </Table.Col>
                <Table.Col className="w-[42%] !p-8 align-middle border-b border-border-primary text-text-tertiary font-light text-xl leading-normal">
                  Ready to import
                </Table.Col>
                <Table.Col
                  align="right"
                  className="w-[20%] !p-8 text-3xl font-medium text-text-primary align-middle border-b border-border-primary">
                  {newCount}
                </Table.Col>
              </Table.Row>
              <Table.Row>
                <Table.Col className="w-[38%] !p-8 align-middle border-b border-border-primary">
                  <span className="inline-flex items-center gap-4 text-xl font-medium text-text-primary">
                    <HugeiconsIcon
                      icon={InformationCircleIcon}
                      className="text-text-status-passive shrink-0"
                      size={22}
                      strokeWidth={1.5}
                    />
                    <span>Already Exists</span>
                  </span>
                </Table.Col>
                <Table.Col className="w-[42%] !p-8 align-middle border-b border-border-primary text-text-tertiary font-light text-xl leading-normal">
                  Exact Match
                </Table.Col>
                <Table.Col
                  align="right"
                  className="w-[20%] !p-8 text-3xl font-medium text-text-primary align-middle border-b border-border-primary">
                  {exactDbMatchCount}
                </Table.Col>
              </Table.Row>
              {intraCsvDuplicateCount > 0 && (
                <Table.Row>
                  <Table.Col className="w-[38%] !p-8 align-middle border-b border-border-primary">
                    <span className="inline-flex items-center gap-4 text-xl font-medium text-text-primary">
                      <HugeiconsIcon
                        icon={InformationCircleIcon}
                        className="text-text-status-passive shrink-0"
                        size={22}
                        strokeWidth={1.5}
                      />
                      <span>Duplicate in CSV</span>
                    </span>
                  </Table.Col>
                  <Table.Col className="w-[42%] !p-8 align-middle border-b border-border-primary text-text-tertiary font-light text-xl leading-normal">
                    Same email in file
                  </Table.Col>
                  <Table.Col
                    align="right"
                    className="w-[20%] !p-8 text-3xl font-medium text-text-primary align-middle border-b border-border-primary">
                    {intraCsvDuplicateCount}
                  </Table.Col>
                </Table.Row>
              )}
              <Table.Row>
                <Table.Col className="w-[38%] !p-8 align-middle border-b border-border-primary">
                  <span className="inline-flex items-center gap-4 text-xl font-medium text-text-primary">
                    <HugeiconsIcon
                      icon={AlertCircleIcon}
                      className="text-text-status-pending shrink-0"
                      size={22}
                      strokeWidth={1.5}
                    />
                    <span>Needs Review</span>
                  </span>
                </Table.Col>
                <Table.Col className="w-[42%] !p-8 align-middle border-b border-border-primary text-text-tertiary font-light text-xl leading-normal">
                  Possible Duplicates
                </Table.Col>
                <Table.Col
                  align="right"
                  className="w-[20%] !p-8 text-3xl font-medium text-text-primary align-middle border-b border-border-primary">
                  {uniquePossibleRowCount}
                </Table.Col>
              </Table.Row>
            </Table.Body>
            <tfoot>
              <Table.Row className="bg-surface-secondary border-t border-border-primary">
                <Table.Col
                  colSpan={2}
                  className="!p-8 text-xl font-medium text-text-primary !border-b-0">
                  Total
                </Table.Col>
                <Table.Col
                  align="right"
                  className="!p-8 text-3xl font-medium text-text-primary !border-b-0">
                  {totalRows}
                </Table.Col>
              </Table.Row>
            </tfoot>
          </Table>
        </Table.Context>
      </div>

      {/* Accordion: list of duplicate rows (read-only) */}
      {allDuplicates.length > 0 && (
        <div className="bg-white border border-border-primary rounded-2xl overflow-hidden shadow-sm shadow-soft">
          <div className="flex items-center justify-between gap-4 w-full px-8 py-6 bg-white text-text-primary transition-colors duration-200 hover:bg-surface-secondary">
            {/* Same pattern as ToggleSwitchInput + ChecksSelector: IconComponent (gradient circle) + labels */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={duplicatesOpen}
              className="flex flex-1 min-w-0 items-center gap-0 m-0 border-0 bg-transparent text-text-primary font-inherit text-left cursor-pointer select-none rounded-xl -my-1 -mx-1 px-2 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-text-primary"
              onClick={() => setDuplicatesOpen(o => !o)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDuplicatesOpen(o => !o);
                }
              }}>
              <IconComponent icon={UserMultiple02Icon} className="u-margin-right shrink-0" />
              <span className="flex flex-col items-start gap-0.5 min-w-0 flex-1">
                {/* Match ToggleSwitchInput primary label */}
                <span className="primary-text u-font-size-small u-font-weight-500">
                  {uniqueDuplicateRowCount} duplicate{' '}
                  {uniqueDuplicateRowCount === 1 ? singularLabel : entityLabel}
                </span>
                {/* Match ToggleSwitchInput subtext */}
                <span className="secondary-text u-font-size-x-small">
                  {globalDuplicateAction === DUPLICATE_ACTION_SKIP && 'All will be skipped'}
                  {globalDuplicateAction === DUPLICATE_ACTION_CREATE_NEW &&
                    `All will be saved as new ${entityLabel}`}
                  {globalDuplicateAction === DUPLICATE_ACTION_ADD_TO_EXISTING &&
                    `${addToExistingCount} will update existing${skipCount > 0 ? `, ${skipCount} skipped (no match)` : ''}`}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="export-duplicates-csv">Export CSV</Tooltip>}>
                <button
                  type="button"
                  className="flex items-center justify-center w-10 h-10 p-0 border border-border-primary rounded-full bg-surface-secondary text-text-primary cursor-pointer transition-all duration-200 hover:bg-primary-1 hover:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-text-primary"
                  onClick={handleDownloadSkipped}
                  aria-label="Export duplicate rows as CSV">
                  <HugeiconsIcon icon={Download01Icon} size={16} strokeWidth={1.5} />
                </button>
              </OverlayTrigger>
              <button
                type="button"
                aria-expanded={duplicatesOpen}
                aria-label={duplicatesOpen ? 'Collapse duplicate list' : 'Expand duplicate list'}
                className="flex items-center justify-center w-10 h-10 p-0 border-0 rounded-full bg-transparent text-text-tertiary cursor-pointer transition-colors duration-200 hover:text-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-text-primary"
                onClick={() => setDuplicatesOpen(o => !o)}>
                <HugeiconsIcon
                  icon={ArrowDown01Icon}
                  className={cn('transition-transform duration-200', {
                    'rotate-180': duplicatesOpen,
                  })}
                  size={24}
                  strokeWidth={1.5}
                />
              </button>
            </div>
          </div>
          <Collapse in={duplicatesOpen}>
            <div className="border-t border-border-primary">
              <ul className="list-none m-0 p-0 max-h-[400px] overflow-y-auto flex flex-col gap-0">
                {allDuplicates.map((dup, index) => {
                  if (!dup || typeof dup !== 'object') return null;
                  const existing = dup.existing;
                  const firstName = existing?.first_name ?? existing?.firstName ?? '';
                  const lastName = existing?.last_name ?? existing?.lastName ?? '';
                  const existingEmail = existing?.email ?? '';
                  const isPossible = dup.match_reason === 'name_match';
                  const matchedTo =
                    dup.match_reason === 'intra_csv_duplicate'
                      ? 'Earlier row in CSV'
                      : existing
                        ? `${firstName} ${lastName}`.trim() +
                            (existingEmail ? ` (${existingEmail})` : '') || '—'
                        : '—';
                  const row = dup.csv_row ?? {};
                  const csvName =
                    [row.first_name ?? row.firstName, row.last_name ?? row.lastName]
                      .filter(Boolean)
                      .join(' ') || '—';
                  const csvEmail = row.email ?? '—';
                  const rowNum =
                    typeof dup.csv_index === 'number' && !Number.isNaN(dup.csv_index)
                      ? dup.csv_index
                      : index;
                  const pillText =
                    dup.match_reason === 'intra_csv_duplicate'
                      ? 'Duplicate in CSV'
                      : isPossible
                        ? 'Needs Review'
                        : 'Already Exists';
                  // Match dashboard labels; visual matches CandidatePortalContextSelector StatusPill
                  const candidateStatusDisplay =
                    existing?.status != null
                      ? (CANDIDATE_STATUS_DISPLAY[existing.status] ?? {
                          title: existing.status,
                        })
                      : null;
                  return (
                    <li
                      key={`dup-${rowNum}-${index}`}
                      className="flex flex-col gap-2 px-8 py-4 bg-white border-t border-border-primary first:border-t-0 text-text-primary">
                      {/* Meta: subordinate — portal-style tertiary / secondary */}
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-text-secondary font-medium text-base uppercase tracking-wide leading-tight">
                          Row {rowNum + 1}
                        </span>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`dup-type-${rowNum}-${index}`}>{pillText}</Tooltip>
                          }>
                          <span
                            className={cn(
                              'inline-flex items-center justify-center shrink-0 cursor-default',
                              isPossible ? 'text-text-status-pending' : 'text-text-status-passive',
                            )}
                            aria-label={pillText}>
                            <HugeiconsIcon
                              icon={isPossible ? AlertCircleIcon : InformationCircleIcon}
                              size={20}
                              strokeWidth={1.5}
                            />
                          </span>
                        </OverlayTrigger>
                      </div>
                      {/* Primary title — matches portal company name scale */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-3xl font-normal text-text-primary m-0 leading-[1.15] tracking-tight">
                          {csvName}
                        </span>
                        {candidateStatusDisplay && (
                          <StatusPill
                            className="shrink-0"
                            capitalize
                            small
                            status={existing.status}
                            text={candidateStatusDisplay.title}
                          />
                        )}
                      </div>
                      {csvEmail !== '—' && (
                        <p className="text-text-primary font-light text-xl leading-snug break-all m-0">
                          {csvEmail}
                        </p>
                      )}
                      {/* Secondary detail — portal job_role + primary line pattern */}
                      <p className="m-0 text-xl font-light text-text-secondary leading-normal">
                        <span className="font-light">
                          {isPossible
                            ? 'Possible duplicate with: '
                            : dup.match_reason === 'email_match_employee'
                              ? 'Matches existing employee: '
                              : 'Matches existing candidate: '}
                        </span>
                        <span className="text-xl font-medium text-text-primary break-words leading-normal">
                          {matchedTo}
                        </span>
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </Collapse>
        </div>
      )}

      {/* Summary note / alert */}
      {newCount === 0 && createAnywayCount === 0 && addToExistingCount === 0 ? (
        allDuplicates.length > 0 ? (
          <FormMessage
            type={MessageType.Warning}
            message={`Your CSV contains no new ${entityLabel}. Duplicate handling is currently set to "Skip all duplicates." To import these records, choose either "Save as new ${singularLabel}" or "Update existing ${singularLabel}" below`}
            closeable={false}
            noMargin
            className={reviewAlertClass}
          />
        ) : (
          <FormMessage
            type={MessageType.Warning}
            message={`No ${entityLabel} to import.`}
            closeable={false}
            noMargin
            className={reviewAlertClass}
          />
        )
      ) : allDuplicates.length > 0 ? (
        <FormMessage
          type={MessageType.Info}
          message={
            `${newCount + createAnywayCount + addToExistingCount} ${newCount + createAnywayCount + addToExistingCount === 1 ? singularLabel : entityLabel} will be imported` +
            (newCount > 0 ? ` (${newCount} new)` : '') +
            (createAnywayCount > 0 ? `, ${createAnywayCount} saved as new` : '') +
            (addToExistingCount > 0 ? `, ${addToExistingCount} update existing` : '') +
            '.'
          }
          closeable={false}
          noMargin
          className={reviewAlertClass}
        />
      ) : null}

      {/* Global action for all duplicate rows (radio options) */}
      {allDuplicates.length > 0 && (
        <div className="flex flex-col gap-3">
          {allDuplicatesAreIntraCsv && (
            <FormMessage
              type={MessageType.Warning}
              message={`All duplicate rows are duplicate emails within your file. They will be skipped and cannot be saved as new or added to an existing ${singularLabel}.`}
              closeable={false}
              noMargin
              className={reviewAlertClass}
            />
          )}
          <div className="text-text-primary">
            <p className="text-xl font-medium text-text-primary m-0 mb-2 leading-normal">
              For all duplicate rows
            </p>
            <MultipleChoice
              value={globalDuplicateAction}
              onChange={setGlobalDuplicateAction}
              radioButtonStyle
              options={{
                options: [
                  { name: 'Skip all duplicates', value: DUPLICATE_ACTION_SKIP },
                  { name: `Save as new ${entityLabel}`, value: DUPLICATE_ACTION_CREATE_NEW },
                  {
                    name: `Update existing ${entityLabel}`,
                    value: DUPLICATE_ACTION_ADD_TO_EXISTING,
                  },
                ],
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicatesReview;
